import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Clock, Search, BookOpen, Calendar, ArrowRight, Filter } from 'lucide-react';
import { mockArticles, categories } from '../data/mock';
import SEO, { breadcrumbJsonLd } from '../components/SEO';

const ArticlesListPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date'); // date, readTime, title

  const filteredArticles = mockArticles
    .filter(article => {
      const matchesSearch = !searchQuery || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || article.category === categories.find(c => c.id === selectedCategory)?.name;
      
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.publishedAt) - new Date(a.publishedAt);
        case 'readTime':
          return a.readTime - b.readTime;
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  const allFeatured = filteredArticles.filter(article => article.featured);
  const featuredArticles = allFeatured.slice(0, 3);
  const regularArticles = [
    ...allFeatured.slice(3),
    ...filteredArticles.filter(article => !article.featured)
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <SEO
        title="Tous nos guides experts freelance"
        description="Plus de 65 articles experts pour freelances et entrepreneurs : fiscalité, statuts juridiques, URSSAF, chômage, holding, SCI, optimisation, IA, cession. Données 2026."
        path="/articles"
        jsonLd={breadcrumbJsonLd([
          { name: 'Accueil', path: '/' },
          { name: 'Articles', path: '/articles' }
        ])}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Tous nos guides experts
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plus de 65 articles rédigés par des experts pour vous accompagner dans votre création d'entreprise. 
              Contenus validés et mis à jour régulièrement.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtres et recherche */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Recherche */}
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Rechercher dans les articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Catégories */}
            <div className="md:col-span-4">
              <select 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
              >
                <option value="all">Toutes catégories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Tri */}
            <div className="md:col-span-3">
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
              >
                <option value="date">Plus récents</option>
                <option value="readTime">Lecture rapide</option>
                <option value="title">Ordre alphabétique</option>
              </select>
            </div>
          </div>

          {/* Lien recherche avancée */}
          <div className="mt-4 text-center">
            <Link to="/recherche">
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Recherche avancée
              </Button>
            </Link>
          </div>
        </div>

        {/* Articles à la une */}
        {featuredArticles.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              ⭐ Articles à la une
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredArticles.map((article) => (
                <Card key={article.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0">
                  {/* Image placeholder avec gradient */}
                  <div className="h-48 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 relative overflow-hidden rounded-t-lg">
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-white/90 text-gray-700 border border-gray-200 backdrop-blur-sm font-medium">
                        {article.category}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <div className="flex items-center gap-2 text-white text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        <span>{article.readTime} min</span>
                      </div>
                    </div>
                  </div>

                  <CardHeader>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}</span>
                    </div>
                    
                    <CardTitle className="text-xl font-bold text-gray-900 leading-tight">
                      <Link to={`/article/${article.slug}`} className="hover:text-blue-600 transition-colors">
                        {article.title}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex gap-2 mb-4">
                      {article.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <Link to={`/article/${article.slug}`}>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white group">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Lire l'article
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Tous les articles */}
        {regularArticles.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {featuredArticles.length > 0 ? 'Autres articles' : 'Tous les articles'} 
              ({regularArticles.length})
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {regularArticles.map((article) => (
                <Card key={article.id} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="secondary">{article.category}</Badge>
                      <span className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {article.readTime} min
                      </span>
                    </div>
                    
                    <CardTitle className="text-lg leading-tight">
                      <Link to={`/article/${article.slug}`} className="hover:text-blue-600 transition-colors">
                        {article.title}
                      </Link>
                    </CardTitle>
                    
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                      {article.excerpt.slice(0, 120)}...
                    </p>
                    
                    <div className="flex gap-1 mb-4">
                      {article.tags.slice(0, 2).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <Link to={`/article/${article.slug}`}>
                      <Button variant="outline" size="sm" className="w-full group">
                        Lire l'article
                        <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Aucun résultat */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <BookOpen className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun article trouvé
            </h3>
            <p className="text-gray-600 mb-4">
              Essayez avec d'autres mots-clés ou changez de catégorie
            </p>
            <Button 
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              variant="outline"
            >
              Voir tous les articles
            </Button>
          </div>
        )}

        {/* Newsletter */}
        <div className="mt-16 bg-blue-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">
            Ne manquez aucun article !
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Recevez nos nouveaux guides directement par email et restez à jour sur l'actualité entrepreneuriale.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input 
              type="email" 
              placeholder="Votre email" 
              className="bg-white text-gray-900 border-white"
            />
            <Button className="bg-white text-blue-600 hover:bg-gray-100">
              S'abonner
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlesListPage;